import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

type PipelineStatus = 'active' | 'disabled' | 'conditional' | 'empty';
type PipelineColor = 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray';

interface PipelineBoxProps {
  step: number;
  label: string;
  detail: string;
  priority?: string;
  badges?: string[];
  status: PipelineStatus;
  color: PipelineColor;
  onClick?: () => void;
  children?: React.ReactNode;
}

const colorMap: Record<PipelineColor, { border: string; bg: string; text: string; badge: string }> = {
  blue:   { border: 'border-l-blue-500',   bg: 'bg-blue-50',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700' },
  green:  { border: 'border-l-green-500',  bg: 'bg-green-50',  text: 'text-green-700',  badge: 'bg-green-100 text-green-700' },
  orange: { border: 'border-l-amber-500',  bg: 'bg-amber-50',  text: 'text-amber-700',  badge: 'bg-amber-100 text-amber-700' },
  red:    { border: 'border-l-red-500',    bg: 'bg-red-50',    text: 'text-red-700',    badge: 'bg-red-100 text-red-700' },
  purple: { border: 'border-l-purple-500', bg: 'bg-purple-50', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
  gray:   { border: 'border-l-gray-400',   bg: 'bg-gray-50',   text: 'text-gray-500',   badge: 'bg-gray-100 text-gray-600' },
};

const statusStyles: Record<PipelineStatus, string> = {
  active: 'border-l-4',
  disabled: 'border-l-4 opacity-60',
  conditional: 'border-l-4 border-dashed',
  empty: 'border-l-4 border-dotted opacity-50',
};

const statusDot: Record<PipelineStatus, string> = {
  active: 'bg-green-500',
  disabled: 'bg-gray-400',
  conditional: 'bg-amber-500',
  empty: 'bg-gray-300',
};

const PipelineBox = ({ step, label, detail, priority, badges, status, color, onClick, children }: PipelineBoxProps) => {
  const [expanded, setExpanded] = useState(false);
  const colors = colorMap[color];
  const hasChildren = !!children;

  const handleClick = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <div
      className={`rounded-lg border ${statusStyles[status]} ${colors.border} ${colors.bg} ${hasChildren || onClick ? 'cursor-pointer hover:shadow-sm' : ''} transition-all`}
      onClick={handleClick}
    >
      <div className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 min-w-0">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
              {step}
            </span>
            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-sm text-gray-900">{label}</span>
                {priority && (
                  <span className="text-xs text-gray-400">Priority {priority}</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
              {badges && badges.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {badges.map((badge, i) => (
                    <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors.badge}`}>
                      {badge}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
            <div className={`w-2 h-2 rounded-full ${statusDot[status]}`} />
            {hasChildren && (
              expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>
      {expanded && children && (
        <div className="border-t border-gray-200 px-3 pb-3 pt-2" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
    </div>
  );
};

export default PipelineBox;
